import { usePage } from "@inertiajs/react"
import { ImageIcon, Sparkles } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { toast } from "sonner"
import { CanvasStylesModal } from "@/components/canvas/canvas-styles-modal"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { GenerationAttributeBar } from "@/components/media/generation-attribute-bar"
import { ProjectStylesToggle } from "@/components/media/project-styles-toggle"
import { PromptEditor, type PromptEditorHandle } from "@/components/prompt-editor/prompt-editor"
import { ReferenceStrip, type ReferenceStripItem } from "@/components/references/reference-strip"
import type { MentionItem } from "@/components/prompt-editor/types"
import { aspectRatiosToFrameFormats } from "@/components/ui/frame-format-select"
import type { FloatingTabPillTab } from "@/components/ui/floating-tab-pill"
import { WorkspaceAgentSideColumn } from "@/components/workspace/workspace-agent-side-column"
import { PinnedFooterBox } from "@/components/workspace/pinned-tab-layers"
import type {
  AgentFlowNodePromptUpdatedPayload,
  AgentShotDurationUpdatedPayload,
  AgentShotPromptUpdatedPayload,
} from "@/components/agent/agent-chat-panel"
import { flowNodeKindLabelKey } from "@/components/flow/flow-node-title"
import { FlowPromptComposer, type FlowPromptChrome } from "@/components/flow/flow-prompt-composer"
import { FlowReferenceAdder } from "@/components/flow/flow-reference-adder"
import {
  FlowShotPrompt,
  type FlowShotAgentPending,
  type FlowShotPromptHandle,
} from "@/components/flow/flow-shot-prompt"
import { WorkspaceBottomDock, type WorkspaceBottomDockAgentProps } from "@/components/workspace/workspace-bottom-dock"
import { useGenerationPrice } from "@/hooks/use-generation-price"
import { flowNodeMentionItems } from "@/lib/flow-node-mentions"
import { useTranslation } from "@/hooks/use-translation"
import { generationSettingsForModel } from "@/lib/canvas-model-defaults"
import {
  canvasHasProjectStyles,
  canvasProjectStylesUnavailableReason,
  clampCanvasUseProjectStyles,
} from "@/lib/canvas-project-styles"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { cn } from "@/lib/utils"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import { REFERENCE_IMAGE_FILE_ACCEPT, REFERENCE_IMAGE_VIDEO_FILE_ACCEPT } from "@/lib/reference-file-policy"
import { FlowChainPanel } from "@/components/flow/flow-chain-panel"
import { FlowDepthPanel } from "@/components/flow/flow-depth-panel"
import { FlowTransitionFrameSlots } from "@/components/flow/flow-transition-frame-slots"
import { FlowUpscalePanel } from "@/components/flow/flow-upscale-panel"
import type { FlowSidePanelProps } from "@/components/flow/flow-side-panel-props"
import { assignFlowKeyframe, generateFlowNode, updateFlowNode } from "@/lib/flow-api"
import { durationOptionsForCanvasModel } from "@/lib/video-duration"
import type { CanvasMediaKind, CanvasModel, CanvasProjectStyles } from "@/types/canvas"
import {
  FLOW_FRAMES_PORT,
  FLOW_IMAGE_TARGET,
  FLOW_VIDEO_PORT,
  flowModelsForNode,
  flowNodeAcceptsImageReferences,
  flowNodeCanvasMediaKind,
  flowNodeEntityKind,
  flowNodeGenerates,
  flowNodePending,
  type FlowNodeInput,
  type FlowNodeRow,
} from "@/types/flow"
import type { PostProductionModelOption } from "@/lib/workspace-api"
import type { WorkspaceBaseProps } from "@/types/workspace"
import type { JSONContent } from "@tiptap/core"

function flowNodeModelParams(
  mediaKind: CanvasMediaKind,
  aspectRatio: string,
  durationSec: number | null,
  modelParams: Record<string, string>,
): Record<string, string> {
  const params: Record<string, string> = { ...modelParams, aspect_ratio: aspectRatio }
  if (mediaKind === "video" && durationSec != null) params.duration_sec = String(durationSec)
  return params
}

function providerModelParams(modelParams: Record<string, string>): Record<string, string> {
  const params = { ...modelParams }
  delete params.aspect_ratio
  delete params.duration_sec
  return params
}

function storedDurationSec(params: Record<string, string>): number | null {
  const stored = Number(params.duration_sec)
  return Number.isFinite(stored) ? stored : null
}

/** Превью входа. У героя это референс (раскладка, если есть), без файла на скачивание. */
function referenceMedia(
  input: FlowNodeInput,
  source: FlowNodeRow,
): {
  url: string | null
  thumbUrl: string | null
  downloadUrl: string | null
  downloadable: boolean
} {
  if (source.kind === "character") {
    const preview = input.thumb_url ?? input.url ?? source.media_thumb_url ?? null
    return {
      url: preview,
      thumbUrl: preview,
      downloadUrl: null,
      downloadable: false,
    }
  }
  const fromSheet = Boolean(input.download_url || input.thumb_url || input.url)
  if (fromSheet) {
    const downloadUrl = input.download_url ?? input.url ?? null
    return {
      url: downloadUrl,
      thumbUrl: input.thumb_url ?? input.url ?? downloadUrl,
      downloadUrl,
      downloadable: true,
    }
  }
  return {
    url: source.download_url ?? source.media_url ?? null,
    thumbUrl: source.media_thumb_url ?? null,
    downloadUrl: source.download_url ?? null,
    downloadable: true,
  }
}

type NodePromptPatch = Pick<FlowNodeRow, "prompt_text" | "prompt_doc">

type PendingNodeSave = {
  nodeId: number
  patch: Parameters<typeof updateFlowNode>[2]
}

type PanelArgs = {
  projectId: number
  projectName: string
  activeFlowId: number | null
  node: FlowNodeRow | null
  nodes: FlowNodeRow[]
  models: CanvasModel[]
  upscaleModels: PostProductionModelOption[]
  frameFormat: string
  frameRate?: number
  projectStyles: CanvasProjectStyles
  mentionItems: MentionItem[]
  canEdit: boolean
  onDisconnectEdge: (edgeId: number) => void
  onNodeChange: (node: FlowNodeRow) => void
  onNodesChange: (nodes: FlowNodeRow[]) => void
  /** Patch a node row by id. The open editor is a different node when assistants run in parallel. */
  onNodePatch: (nodeId: number, patch: NodePromptPatch) => void
  onAttachInputs: (targetId: number, batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
  onFlowBoardChanged?: () => void
  /** `@node` tokens in the open composer. Highlight only — does not select the node. */
  onMentionedFlowNodeIdsChange?: (ids: number[]) => void
}

export function useFlowNodePanel({
  projectId,
  projectName,
  activeFlowId,
  node,
  nodes,
  models,
  upscaleModels,
  frameFormat,
  frameRate,
  projectStyles,
  mentionItems,
  canEdit,
  onDisconnectEdge,
  onNodeChange,
  onNodesChange,
  onNodePatch,
  onAttachInputs,
  onFlowBoardChanged,
  onMentionedFlowNodeIdsChange,
}: PanelArgs) {
  const { t } = useTranslation(["flow", "workspace"])
  const seedanceDraftDefault = Boolean(
    usePage<Partial<WorkspaceBaseProps>>().props.project?.byteplus_seedance_draft_default,
  )
  const editorRef = useRef<PromptEditorHandle>(null)
  const shotPromptRef = useRef<FlowShotPromptHandle>(null)
  const pendingShotAgentRef = useRef<{ shotId: number; edit: FlowShotAgentPending } | null>(null)
  const saveTimer = useRef<number | null>(null)
  const pendingSaveRef = useRef<PendingNodeSave | null>(null)
  const saveChainRef = useRef(Promise.resolve())
  const nodeRef = useRef(node)
  const canEditRef = useRef(canEdit)
  const onNodeChangeRef = useRef(onNodeChange)
  const onNodesChangeRef = useRef(onNodesChange)
  const onNodePatchRef = useRef(onNodePatch)
  const projectIdRef = useRef(projectId)
  const tRef = useRef(t)
  const tabRef = useRef<"prompt" | "assistant">("prompt")
  // Skip editor onChange while the open node is being reseeded, so the previous
  // buffer is not PATCHed onto the newly selected node.
  const reseedSkipRef = useRef(false)
  const pendingReseedRef = useRef(false)
  const agentApplyRef = useRef(false)
  const selectedNodeIdRef = useRef<number | null | undefined>(undefined)
  nodeRef.current = node
  canEditRef.current = canEdit
  onNodeChangeRef.current = onNodeChange
  onNodesChangeRef.current = onNodesChange
  onNodePatchRef.current = onNodePatch
  projectIdRef.current = projectId
  tRef.current = t
  const selectedNodeId = node?.id ?? null
  if (selectedNodeIdRef.current !== selectedNodeId) {
    selectedNodeIdRef.current = selectedNodeId
    reseedSkipRef.current = true
    pendingReseedRef.current = true
  } else if (pendingReseedRef.current) {
    // Follow-up render after setText(node.prompt). Keep ignoring editor onChange.
    reseedSkipRef.current = true
    pendingReseedRef.current = false
  }
  const [tab, setTab] = useState<"prompt" | "assistant">("prompt")
  tabRef.current = tab
  const [promptDirty, setPromptDirty] = useState(false)
  const [text, setText] = useState("")
  const [doc, setDoc] = useState<JSONContent | null>(null)
  const [modelId, setModelId] = useState("")
  const [aspectRatio, setAspectRatio] = useState(frameFormat)
  const [durationSec, setDurationSec] = useState<number | null>(null)
  const [modelParams, setModelParams] = useState<Record<string, string>>({})
  const [useProjectStyles, setUseProjectStyles] = useState(true)
  const [stylesOpen, setStylesOpen] = useState(false)
  const [generating, setGenerating] = useState(false)

  const generative = node != null && flowNodeGenerates(node.kind)
  const enabledModels = useMemo(
    () => (node && generative ? flowModelsForNode(node.kind, models) : []),
    [generative, models, node],
  )
  const selectedModel = enabledModels.find((model) => model.id === modelId) ?? enabledModels[0] ?? null

  const commitSave = useCallback((pending: PendingNodeSave | null) => {
    if (!pending || !canEditRef.current) return
    const fail = tRef.current("errors.nodeFailed")
    saveChainRef.current = saveChainRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const next = await updateFlowNode(projectIdRef.current, pending.nodeId, pending.patch)
          onNodeChangeRef.current(next)
        } catch (error: unknown) {
          toast.error(error instanceof Error ? error.message : fail)
        }
      })
  }, [])

  const takePendingSave = useCallback((): PendingNodeSave | null => {
    const pending = pendingSaveRef.current
    pendingSaveRef.current = null
    if (saveTimer.current != null) {
      window.clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    return pending
  }, [])

  const flushPendingNodeSaves = useCallback(() => {
    commitSave(takePendingSave())
  }, [commitSave, takePendingSave])

  useEffect(() => {
    // Flush the edit that belonged to the previous node. Do not retarget it.
    flushPendingNodeSaves()
    if (!node) return
    setPromptDirty(false)
    setTab(flowNodeGenerates(node.kind) || node.kind === "shot" ? "prompt" : "assistant")
    setText(node.prompt_text)
    setDoc(node.prompt_doc)
    const matching = flowModelsForNode(node.kind, models)
    const nextModel = matching.some((model) => model.id === node.model_id)
      ? (node.model_id ?? "")
      : (matching[0]?.id ?? "")
    setModelId(nextModel)
    if (flowNodeGenerates(node.kind)) {
      const model = matching.find((item) => item.id === nextModel) ?? null
      const canvasKind = flowNodeCanvasMediaKind(node.kind)
      if (!canvasKind) return
      const next = generationSettingsForModel(
        model,
        canvasKind,
        node.model_params.aspect_ratio || frameFormat,
        storedDurationSec(node.model_params),
        frameFormat,
        node.model_params,
      )
      setAspectRatio(next.aspectRatio)
      setDurationSec(next.durationSec)
      setModelParams(next.modelParams)
      setUseProjectStyles(clampCanvasUseProjectStyles(node.use_project_styles, projectStyles))
      setStylesOpen(false)
    }
    // Reseed only when the selected node changes. Prompt saves update the same node.
    // reseedSkipRef is set during render, before the editor effect emits onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id])

  useEffect(() => {
    return () => {
      flushPendingNodeSaves()
    }
  }, [flushPendingNodeSaves])

  // After every commit's editor effects. The follow-up reseed render raises the flag
  // again, so that onChange is skipped too; this then clears it. No deps on purpose.
  useEffect(() => {
    reseedSkipRef.current = false
  })

  const persist = useCallback(
    (patch: Parameters<typeof updateFlowNode>[2]) => {
      const current = nodeRef.current
      if (!current || !canEditRef.current) return
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current)
      const previous = pendingSaveRef.current
      if (previous && previous.nodeId !== current.id) commitSave(previous)
      const base = previous?.nodeId === current.id ? previous.patch : {}
      pendingSaveRef.current = { nodeId: current.id, patch: { ...base, ...patch } }
      saveTimer.current = window.setTimeout(() => {
        saveTimer.current = null
        commitSave(takePendingSave())
      }, 500)
    },
    [commitSave, takePendingSave],
  )

  const handlePromptChange = useCallback(
    (nextText: string, nextDoc: JSONContent) => {
      if (reseedSkipRef.current) return
      if (agentApplyRef.current) {
        setText(nextText)
        setDoc(nextDoc)
        return
      }
      setText(nextText)
      setDoc(nextDoc)
      persist({ prompt_text: nextText, prompt_doc: nextDoc })
    },
    [persist],
  )

  const applyAgentPrompt = useCallback((payload: AgentFlowNodePromptUpdatedPayload) => {
    const promptDoc = (payload.promptDoc as JSONContent | null) ?? null
    if (pendingSaveRef.current?.nodeId === payload.nodeId) {
      pendingSaveRef.current = null
      if (saveTimer.current != null) {
        window.clearTimeout(saveTimer.current)
        saveTimer.current = null
      }
    }
    onNodePatchRef.current(payload.nodeId, { prompt_text: payload.promptText, prompt_doc: promptDoc })

    const current = nodeRef.current
    if (!current || payload.nodeId !== current.id) return

    agentApplyRef.current = true
    try {
      setText(payload.promptText)
      setDoc(promptDoc)
      editorRef.current?.setExternalText(payload.promptText)
    } finally {
      agentApplyRef.current = false
    }
    if (tabRef.current !== "prompt") setPromptDirty(true)
    toast.success(tRef.current("agent.promptUpdated", { ns: "workspace" }))
  }, [])

  const selectPromptTab = useCallback(() => {
    setTab("prompt")
    setPromptDirty(false)
  }, [])

  const mediaKind: CanvasMediaKind | null = node ? flowNodeCanvasMediaKind(node.kind) : null
  const transitionHasFrame =
    node?.kind === "video_transition" && (node.inputs ?? []).some((input) => input.target_port === FLOW_FRAMES_PORT)

  const persistDraft = useCallback(
    (
      nextModelId: string,
      nextAspect: string,
      nextDuration: number | null,
      nextParams: Record<string, string>,
      kind: CanvasMediaKind,
    ) => {
      persist({
        model_id: nextModelId,
        model_params: flowNodeModelParams(kind, nextAspect, nextDuration, nextParams),
      })
    },
    [persist],
  )

  const handleModelChange = useCallback(
    (nextModelId: string) => {
      if (!mediaKind) return
      const model = enabledModels.find((item) => item.id === nextModelId) ?? null
      const next = generationSettingsForModel(model, mediaKind, aspectRatio, durationSec, frameFormat, modelParams)
      setModelId(nextModelId)
      setAspectRatio(next.aspectRatio)
      setDurationSec(next.durationSec)
      setModelParams(next.modelParams)
      persistDraft(nextModelId, next.aspectRatio, next.durationSec, next.modelParams, mediaKind)
    },
    [aspectRatio, durationSec, enabledModels, frameFormat, mediaKind, modelParams, persistDraft],
  )

  const handleAspectChange = useCallback(
    (nextAspect: string) => {
      if (!mediaKind) return
      setAspectRatio(nextAspect)
      persistDraft(modelId, nextAspect, durationSec, modelParams, mediaKind)
    },
    [durationSec, mediaKind, modelId, modelParams, persistDraft],
  )

  const handleDurationChange = useCallback(
    (nextDuration: number) => {
      if (!mediaKind) return
      setDurationSec(nextDuration)
      persistDraft(modelId, aspectRatio, nextDuration, modelParams, mediaKind)
    },
    [aspectRatio, mediaKind, modelId, modelParams, persistDraft],
  )

  const handleModelParamsChange = useCallback(
    (nextParams: Record<string, string>) => {
      if (!mediaKind) return
      setModelParams(nextParams)
      persistDraft(modelId, aspectRatio, durationSec, nextParams, mediaKind)
    },
    [aspectRatio, durationSec, mediaKind, modelId, persistDraft],
  )

  const handleProjectStylesToggle = useCallback(() => {
    if (!canvasHasProjectStyles(projectStyles)) return
    const next = !useProjectStyles
    setUseProjectStyles(next)
    persist({ use_project_styles: next })
  }, [persist, projectStyles, useProjectStyles])

  const handleGenerate = useCallback(async () => {
    if (!node || !canEdit || !selectedModel) return
    const snapshot = editorRef.current?.getSnapshot()
    const promptText = snapshot?.text ?? text
    const promptDoc = snapshot?.doc ?? doc
    setGenerating(true)
    try {
      const next = await generateFlowNode(projectId, node.id, {
        prompt_text: promptText,
        prompt_doc: promptDoc,
        model_id: selectedModel.id,
        aspect_ratio: aspectRatio,
        duration_sec: mediaKind === "video" ? durationSec : null,
        model_params: providerModelParams(modelParams),
        use_project_styles: clampCanvasUseProjectStyles(useProjectStyles, projectStyles),
      })
      onNodeChange(next)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("errors.generateFailed"))
    } finally {
      setGenerating(false)
    }
  }, [
    aspectRatio,
    canEdit,
    doc,
    durationSec,
    modelParams,
    node,
    onNodeChange,
    projectId,
    projectStyles,
    selectedModel,
    t,
    text,
    mediaKind,
    useProjectStyles,
  ])

  const durationOptions = useMemo(
    () => (mediaKind === "video" && selectedModel ? durationOptionsForCanvasModel(selectedModel) : []),
    [mediaKind, selectedModel],
  )
  const formatOptions = useMemo(() => aspectRatiosToFrameFormats(selectedModel?.aspect_ratios ?? []), [selectedModel])
  const currentDuration = durationSec ?? durationOptions[0] ?? 5

  const price = useGenerationPrice({
    kind: "canvas_generate",
    projectId,
    enabled: Boolean(node && generative && selectedModel && !generating),
    params: {
      model_id: selectedModel?.id ?? "",
      media_kind: mediaKind ?? "image",
      duration_sec: mediaKind === "video" ? durationSec : null,
      resolution: modelParams.resolution ?? null,
      image_size: modelParams.image_size ?? null,
      generate_audio: modelParams.generate_audio === "true",
    },
  })

  const subjectMissing = nodeSubjectMissing(node)
  const subjectMissingLabel = t(nodeSubjectMissingKey(node))
  const referenceSlots = useMemo(() => {
    if (!node || !flowNodeAcceptsImageReferences(node)) return []
    const byId = new Map(nodes.map((row) => [row.id, row]))
    const imageInputs = (node.inputs ?? []).filter(
      (input) => (input.target_port ?? FLOW_IMAGE_TARGET) === FLOW_IMAGE_TARGET,
    )
    const slots = imageInputs.flatMap((input, index) => {
      const source = byId.get(input.source_node_id)
      if (!source) return []
      const position = index + 1
      return [
        {
          edgeId: input.id,
          position,
          label: `@reference_${position}`,
          source,
          media: referenceMedia(input, source),
          mediaType: "image" as const,
        },
      ]
    })
    if (node.kind !== "video") return slots
    const videoInput = (node.inputs ?? []).find((input) => input.target_port === FLOW_VIDEO_PORT)
    if (!videoInput) return slots
    const source = byId.get(videoInput.source_node_id)
    if (!source) return slots
    const position = imageInputs.length + 1
    return [
      ...slots,
      {
        edgeId: videoInput.id,
        position,
        label: `@reference_${position}`,
        source,
        media: {
          url: source.media_url ?? source.download_url ?? null,
          thumbUrl: source.media_thumb_url ?? null,
          downloadUrl: source.download_url ?? source.media_url ?? null,
          downloadable: true,
        },
        mediaType: "video" as const,
      },
    ]
  }, [node, nodes])
  const referenceStrip = useMemo<ReferenceStripItem[]>(
    () =>
      referenceSlots.map((slot) => ({
        key: String(slot.edgeId),
        url: slot.media.url,
        thumbUrl: slot.media.thumbUrl,
        downloadUrl: slot.media.downloadUrl,
        downloadable: slot.media.downloadable,
        mediaType: slot.mediaType,
        label: slot.label,
      })),
    [referenceSlots],
  )
  const promptMentionItems = useMemo<MentionItem[]>(() => {
    if (referenceSlots.length === 0) return mentionItems
    const references: MentionItem[] = referenceSlots.map((slot) => ({
      category: "reference",
      id: slot.position,
      label: slot.label,
      imageUrl: slot.media.thumbUrl ?? (slot.mediaType === "image" ? slot.media.url : null),
      mediaType: slot.mediaType,
    }))
    return mentionItems.concat(references)
  }, [mentionItems, referenceSlots])
  const insertReferenceMention = useCallback((label: string) => {
    editorRef.current?.insertReference(label)
  }, [])

  const makeKeyframe = useCallback(async (sourceNodeId: number) => {
    const current = nodeRef.current
    if (!current || !canEditRef.current) return
    try {
      const result = await assignFlowKeyframe(projectIdRef.current, current.id, sourceNodeId)
      onNodesChangeRef.current(result.nodes)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : tRef.current("errors.nodeFailed"))
    }
  }, [])

  const tabs = useMemo<FloatingTabPillTab[]>(() => {
    if (!node || subjectMissing) return []
    if (!generative && node.kind !== "shot") {
      return [
        {
          id: "assistant",
          label: t("column.assistant"),
          active: true,
          icon: <Sparkles className="h-3.5 w-3.5" />,
          onClick: () => setTab("assistant"),
        },
      ]
    }
    return [
      {
        id: "prompt",
        label: t("column.prompt"),
        active: tab === "prompt",
        dirty: generative && promptDirty,
        icon: <ImageIcon className="h-3.5 w-3.5" />,
        onClick: selectPromptTab,
      },
      {
        id: "assistant",
        label: t("column.assistant"),
        active: tab === "assistant",
        icon: <Sparkles className="h-3.5 w-3.5" />,
        onClick: () => setTab("assistant"),
      },
    ]
  }, [generative, node, promptDirty, selectPromptTab, subjectMissing, t, tab])

  const rememberShotAgent = useCallback((shotId: number, edit: FlowShotAgentPending) => {
    const pending = pendingShotAgentRef.current
    const base = pending?.shotId === shotId ? pending.edit : {}
    pendingShotAgentRef.current = { shotId, edit: { ...base, ...edit } }
  }, [])

  const consumePendingShotAgent = useCallback((shotId: number): FlowShotAgentPending | null => {
    const pending = pendingShotAgentRef.current
    if (!pending || pending.shotId !== shotId) return null
    pendingShotAgentRef.current = null
    return pending.edit
  }, [])

  const handleShotPromptUpdated = useCallback(
    (payload: AgentShotPromptUpdatedPayload) => {
      const current = nodeRef.current
      if (!current || current.kind !== "shot" || current.shot_id !== payload.shotId) return
      if (payload.target !== "video") return
      const edit = { text: payload.improvedPrompt, cameraPresetIds: payload.cameraPresetIds }
      if (shotPromptRef.current) shotPromptRef.current.applyExternalPrompt(edit)
      else rememberShotAgent(payload.shotId, edit)
      toast.success(tRef.current("agent.promptUpdated", { ns: "workspace" }))
    },
    [rememberShotAgent],
  )

  const handleShotDurationUpdated = useCallback(
    (payload: AgentShotDurationUpdatedPayload) => {
      const current = nodeRef.current
      if (!current || current.kind !== "shot" || current.shot_id !== payload.shotId) return
      if (shotPromptRef.current) shotPromptRef.current.applyDuration(payload.durationTargetSec)
      else rememberShotAgent(payload.shotId, { durationTargetSec: payload.durationTargetSec })
      toast.success(tRef.current("agent.durationUpdated", { ns: "workspace", sec: payload.durationTargetSec }))
    },
    [rememberShotAgent],
  )

  const nodeMentionItems = useMemo(
    () =>
      flowNodeMentionItems(nodes, (kind) => {
        const key = flowNodeKindLabelKey(kind)
        return key ? t(key) : kind
      }),
    [nodes, t],
  )

  const agent = useMemo<WorkspaceBottomDockAgentProps | null>(() => {
    const mentionHighlight = { onMentionedFlowNodeIdsChange }
    if (node?.kind === "shot") {
      if (node.shot_id == null) return null
      const label = node.shot_label
      return {
        projectId,
        projectName,
        mode: "shots_assistant",
        contextType: "shot",
        contextId: node.shot_id,
        selectedShotId: node.shot_id,
        quickSurface: "shot",
        shotLabel: label,
        shotRefs: label ? [{ id: node.shot_id, label }] : [],
        mentionItems: promptMentionItems,
        onShotPromptUpdated: handleShotPromptUpdated,
        onShotDurationUpdated: handleShotDurationUpdated,
        ...mentionHighlight,
      }
    }
    if (node && flowNodeEntityKind(node.kind)) {
      return entityAgent(node, projectId, projectName, promptMentionItems, onMentionedFlowNodeIdsChange)
    }
    if (node?.kind === "video_upscale" || node?.kind === "video_depth" || node?.kind === "chain") return null
    if (node) {
      return {
        projectId,
        projectName,
        mode: "flow_assistant",
        contextType: "flow_node",
        contextId: node.id,
        quickSurface: "project",
        mentionItems: promptMentionItems,
        title: t("column.nodeAssistant"),
        onFlowNodePromptUpdated: applyAgentPrompt,
        ...mentionHighlight,
      }
    }
    if (activeFlowId == null) return null
    return {
      projectId,
      projectName,
      mode: "flow_assistant",
      contextType: "flow",
      contextId: activeFlowId,
      quickSurface: "project",
      mentionItems: promptMentionItems,
      nodeMentionItems,
      title: t("column.boardAssistant"),
      onFlowBoardChanged,
      ...mentionHighlight,
    }
  }, [
    activeFlowId,
    applyAgentPrompt,
    onFlowBoardChanged,
    onMentionedFlowNodeIdsChange,
    handleShotDurationUpdated,
    handleShotPromptUpdated,
    node,
    nodeMentionItems,
    projectId,
    projectName,
    promptMentionItems,
    t,
  ])

  /** Без chrome — мобильный док; в колонке промпт на всю высоту, генерация в `footerHost`. */
  const promptBody = (chrome?: FlowPromptChrome) => {
    if (node?.kind === "shot" && node.shot_id != null) {
      return (
        <FlowShotPrompt
          // Новый шот — новый экземпляр: размонтирование старого дописывает его промпт.
          key={node.shot_id}
          ref={shotPromptRef}
          projectId={projectId}
          frameFormat={frameFormat}
          frameRate={frameRate}
          shotId={node.shot_id}
          node={node}
          canEdit={canEdit}
          onDisconnect={onDisconnectEdge}
          onMakeKeyframe={makeKeyframe}
          onAttach={(batch) => onAttachInputs(node.id, batch)}
          onNodesChange={onNodesChange}
          consumePendingAgent={consumePendingShotAgent}
          chrome={chrome}
        />
      )
    }
    if (!node || !generative || !mediaKind) return null
    const pending = flowNodePending(node) || generating
    const stylesEnabled = canvasHasProjectStyles(projectStyles)
    const needsFrame = node.kind === "video_transition" && !transitionHasFrame
    const videoFileOpen =
      node.kind === "video" && !(node.inputs ?? []).some((input) => input.target_port === FLOW_VIDEO_PORT)
    return (
      <>
        <div className={cn("flex min-h-0 flex-1 flex-col gap-2", chrome?.fill ? "h-full pb-2" : "py-2")}>
          {node.kind === "video_transition" ? (
            <FlowTransitionFrameSlots
              node={node}
              nodes={nodes}
              projectId={projectId}
              frameFormat={frameFormat}
              frameRate={frameRate}
              canEdit={canEdit}
              locked={pending}
              onDisconnectEdge={onDisconnectEdge}
              onNodeChange={onNodeChange}
              onAttach={(batch) => onAttachInputs(node.id, batch)}
            />
          ) : null}
          {flowNodeAcceptsImageReferences(node) && (referenceStrip.length > 0 || canEdit) ? (
            <ReferenceStrip
              references={referenceStrip}
              projectId={projectId}
              layout="scroll"
              ariaLabel={t("edges.references")}
              onInsertMention={canEdit ? insertReferenceMention : undefined}
              onRemove={
                canEdit
                  ? (index) => {
                      const item = referenceStrip[index]
                      if (item) onDisconnectEdge(Number(item.key))
                    }
                  : undefined
              }
              trailing={
                canEdit ? (
                  <FlowReferenceAdder
                    projectId={projectId}
                    frameFormat={frameFormat}
                    frameRate={frameRate}
                    accepts={videoFileOpen ? ["image", "video"] : ["image"]}
                    fileAccept={videoFileOpen ? REFERENCE_IMAGE_VIDEO_FILE_ACCEPT : REFERENCE_IMAGE_FILE_ACCEPT}
                    onAttach={(batch) => onAttachInputs(node.id, batch)}
                  />
                ) : undefined
              }
            />
          ) : null}
          <FlowPromptComposer
            projectId={projectId}
            className="min-h-0 flex-1"
            footerHost={chrome?.footerHost}
            editor={
              <PromptEditor
                ref={editorRef}
                className={canEdit ? "pb-10" : undefined}
                valueText={text}
                valueDoc={doc}
                seedKey={node.id}
                mentionItems={promptMentionItems}
                onChange={handlePromptChange}
                showSegmentButton={false}
                showMentionHint={false}
                placeholder={t("column.promptPlaceholder")}
                ariaLabel={t("column.prompt")}
                editable={canEdit}
                fillAvailable={chrome?.fill}
              />
            }
            canEdit={canEdit}
            dictationDisabled={pending}
            onDictation={(dictated) => editorRef.current?.insertTextAtCursor(dictated)}
            generate={
              <GenerateWithModelLabel
                label={t("column.generate")}
                busyLabel={t("column.generating")}
                cost={price}
                costPlacement="tooltip"
                busy={pending}
                disabled={node.batch_waiting === true || !selectedModel || text.trim().length === 0 || needsFrame}
                disabledReason={
                  node.batch_waiting ? t("bulk.queued") : needsFrame ? t("column.framesRequired") : undefined
                }
                onClick={handleGenerate}
                confirmDelayKey={confirmDelayKey("flow", node.id, "generate")}
                size="sm"
                buttonClassName="h-8 w-8 shrink-0 p-0 sm:w-auto sm:px-3"
                labelClassName="hidden sm:inline"
                className="shrink-0"
              />
            }
            attributes={
              <>
                <GenerationAttributeBar
                  mediaKind={mediaKind}
                  modelId={selectedModel?.id ?? ""}
                  modelOptions={enabledModels.map((model) => ({ value: model.id, label: model.label }))}
                  onSelectModel={handleModelChange}
                  selectedModel={selectedModel}
                  disabled={!canEdit || pending}
                  aspectRatio={aspectRatio}
                  formatOptions={formatOptions}
                  onAspectRatio={handleAspectChange}
                  durationSec={currentDuration}
                  durationOptions={durationOptions}
                  onDuration={handleDurationChange}
                  modelParams={modelParams}
                  onModelParams={handleModelParamsChange}
                  seedanceDraftDefault={seedanceDraftDefault}
                />
              </>
            }
            attributesTrailing={
              <ProjectStylesToggle
                enabled={stylesEnabled}
                checked={useProjectStyles}
                unavailableReason={canvasProjectStylesUnavailableReason(projectStyles)}
                onToggle={handleProjectStylesToggle}
                onView={() => setStylesOpen(true)}
                disabled={!canEdit || pending}
              />
            }
          />
        </div>
        <CanvasStylesModal open={stylesOpen} onOpenChange={setStylesOpen} styles={projectStyles} />
      </>
    )
  }

  const sidePanel: FlowSidePanelProps | null = node
    ? {
        node,
        nodes,
        projectId,
        frameFormat,
        frameRate,
        canEdit,
        onDisconnectEdge,
        onNodeChange,
        onAttach: (batch) => onAttachInputs(node.id, batch),
      }
    : null

  return {
    node,
    tabs,
    agent,
    subjectMissing,
    subjectMissingLabel,
    dirtyAriaLabel: t("agent.dock.dirtyAria", { ns: "workspace" }),
    profileActive: Boolean(node && tab === "prompt" && (generative || node.kind === "shot") && !subjectMissing),
    promptBody,
    chainBody: sidePanel && node?.kind === "chain" ? <FlowChainPanel {...sidePanel} /> : null,
    upscaleBody:
      sidePanel && node?.kind === "video_upscale" ? <FlowUpscalePanel {...sidePanel} models={upscaleModels} /> : null,
    depthBody: sidePanel && node?.kind === "video_depth" ? <FlowDepthPanel {...sidePanel} /> : null,
  }
}

export function FlowNodeSideColumn({ panel }: { panel: ReturnType<typeof useFlowNodePanel> }) {
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null)
  const customBody = panel.chainBody ?? panel.upscaleBody ?? panel.depthBody
  if (customBody) {
    return <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden">{customBody}</aside>
  }
  if (panel.subjectMissing) {
    return (
      <aside className="flex h-full min-h-0 w-full items-center justify-center p-4 text-sm text-ui-text-muted">
        {panel.subjectMissingLabel}
      </aside>
    )
  }
  if (!panel.agent) return null
  return (
    <WorkspaceAgentSideColumn
      tabs={panel.tabs}
      profileActive={panel.profileActive}
      pinnedFooter
      agent={panel.agent}
      dirtyAriaLabel={panel.dirtyAriaLabel}
      bodySlot={panel.promptBody({ fill: true, footerHost })}
    >
      <PinnedFooterBox>
        <div ref={setFooterHost} className="min-w-0" />
      </PinnedFooterBox>
    </WorkspaceAgentSideColumn>
  )
}

export function FlowNodeDock({ panel }: { panel: ReturnType<typeof useFlowNodePanel> }) {
  const [collapsed, setCollapsed] = useState(false)
  const customBody = panel.chainBody ?? panel.upscaleBody ?? panel.depthBody
  if (customBody) {
    return <div className="max-h-[50vh] overflow-y-auto px-3 py-2">{customBody}</div>
  }
  if (panel.subjectMissing) {
    return <div className="px-4 py-3 text-sm text-ui-text-muted">{panel.subjectMissingLabel}</div>
  }
  if (!panel.agent) return null
  return (
    <WorkspaceBottomDock
      tabs={panel.tabs}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      assistantActive={!panel.profileActive}
      agent={panel.agent}
      dirtyAriaLabel={panel.dirtyAriaLabel}
    >
      {() => panel.promptBody() as ReactNode}
    </WorkspaceBottomDock>
  )
}

function nodeSubjectMissing(node: FlowNodeRow | null): boolean {
  if (!node) return false
  if (node.kind === "shot") return node.shot_id == null
  if (node.kind === "character") return node.character_id == null
  if (node.kind === "location") return node.location_id == null
  if (node.kind === "prop") return node.prop_id == null
  return false
}

function nodeSubjectMissingKey(
  node: FlowNodeRow | null,
): "node.characterMissing" | "node.locationMissing" | "node.propMissing" | "node.shotMissing" {
  if (node?.kind === "character") return "node.characterMissing"
  if (node?.kind === "location") return "node.locationMissing"
  if (node?.kind === "prop") return "node.propMissing"
  return "node.shotMissing"
}

function entityAgent(
  node: FlowNodeRow,
  projectId: number,
  projectName: string,
  mentionItems: MentionItem[],
  onMentionedFlowNodeIdsChange?: (ids: number[]) => void,
): WorkspaceBottomDockAgentProps | null {
  const selectedEntity = {
    name: node.entity_name ?? "",
    imageUrl: node.media_thumb_url,
  }
  if (node.kind === "character" && node.character_id != null) {
    return {
      projectId,
      projectName,
      mode: "characters_assistant",
      contextType: "character",
      contextId: node.character_id,
      selectedEntityId: node.character_id,
      selectedEntity,
      quickSurface: "characters",
      mentionItems,
      onMentionedFlowNodeIdsChange,
    }
  }
  if (node.kind === "location" && node.location_id != null) {
    return {
      projectId,
      projectName,
      mode: "locations_assistant",
      contextType: "location",
      contextId: projectId,
      selectedEntityId: node.location_id,
      selectedEntity,
      quickSurface: "locations",
      mentionItems,
      onMentionedFlowNodeIdsChange,
    }
  }
  if (node.kind === "prop" && node.prop_id != null) {
    return {
      projectId,
      projectName,
      mode: "props_assistant",
      contextType: "prop",
      contextId: projectId,
      selectedEntityId: node.prop_id,
      selectedEntity,
      quickSurface: "props",
      mentionItems,
      onMentionedFlowNodeIdsChange,
    }
  }
  return null
}
